import cn from "classnames";
import DistributionBids from "components/distributionBids/list";
import { Transition } from "components/shared/column";
import Loader from "components/shared/loader";
import differenceInSeconds from "date-fns/differenceInSeconds";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import { viewContainer } from "helpers/redux";
import { ymEvent } from "helpers/system";
import { useLocalStorage } from "hooks/useLocalStorage";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getPlanItem } from "sources/books/reports";
import { increase } from "sources/counter";
import { showBidCarsPlanningSettings } from "sources/profile/organization";
import CityReportContainer from "./cityPlanning";
import Connection from "./connection";

function CityPlanning(props) {
    const { organization, match, location, history } = props;

    const [params, setParams] = useLocalStorage("planningRequestFilters", {});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState({});
    const [dataLoadedAt, setDataLoadedAt] = useState(null);
    const [showOrders, setShowOrders] = useState(false);

    const refreshTimer = useRef(null);
    const updateFrequencyRef = useRef(5 * 60);
    const loadDataRef = useRef();

    useEffect(() => {
        ymEvent("PlanningClicked");
        increase({ Name: "PlanningOpens" });
        setData({});

        showBidCarsPlanningSettings().then((res) => {
            updateFrequencyRef.current = (res.updateFrequency || 5) * 60;
            createInterval();

            const { showAllDraftsInPlanning } = organization || {};
            if (showAllDraftsInPlanning) {
                setParams((prev) => ({ ...prev, showDraftInPlanning: showAllDraftsInPlanning }));
            }
            setTimeout(() => loadData(), 0);
        });

        window.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            clearInterval(refreshTimer.current);
            window.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    const createInterval = () => {
        if (document.visibilityState === "hidden") {
            return;
        }
        clearInterval(refreshTimer.current);
        refreshTimer.current = setInterval(() => {
            if (loadDataRef.current) {
                loadDataRef.current();
            }
        }, updateFrequencyRef.current * 1000);
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            clearInterval(refreshTimer.current);
        } else {
            if (dataLoadedAt && differenceInSeconds(new Date(), new Date(dataLoadedAt)) > updateFrequencyRef.current) {
                loadData();
            }
            createInterval();
        }
    };

    const dataRequest = useCallback((p) => getPlanItem({ ...p, id: "BidCarsPlanningReport" }), []);

    const loadData = () => {
        setIsLoading(true);
        return dataRequest(params).then((d) => {
            setData(d);
            setIsLoading(false);
            setIsLoaded(true);
            setDataLoadedAt(Date.now());
        });
    };

    useEffect(() => {
        loadDataRef.current = loadData;
    }, [loadData]);

    const setParam = (name, value) => {
        setParams((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (isLoading) {
            return;
        }
        loadData();
    }, [params]);

    const resetRequestParams = () => {
        setParams({});
    };

    const renderHead = useCallback(() => {
        const dataFrom = dataLoadedAt ? new Date(dataLoadedAt) : null;
        return (
            <div className="book-show__head pl-0">
                <div className="book-show__title book-show__title--fixed-height w-100">
                    <div className="d-flex align-items-center h-100 justify-content-between">
                        <div>Планирование по городам</div>
                        <div className="lh-sm text-right">
                            {dataFrom && (
                                <div className="text-muted text-nowrap small">
                                    Данные обновлены в{" "}
                                    {format(dataFrom, isToday(dataFrom) ? "HH:mm" : "dd.MM.yyyy HH:mm")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [dataLoadedAt]);

    const renderBodyWrap = () => {
        if (!isLoaded) {
            return <Loader prefix="absolute-center" />;
        }
        return (
            <div className="book-list h-100">
                {isLoading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}
                <Connection data={data}>
                    <CityReportContainer
                        key={data.id}
                        {...props}
                        params={params}
                        data={data}
                        isLoading={isLoading}
                        isLoaded={isLoaded}
                        resetRequestParams={resetRequestParams}
                        refreshData={loadData}
                        setParam={setParam}
                    />
                </Connection>
            </div>
        );
    };

    return (
        <div className="page-content">
            <Transition dir={showOrders ? "half" : "full"} {...{ match, location, history }}>
                <div className="page-book__show book-show">
                    {renderHead()}
                    <div className={cn("book-show__body", { "book-show__body--invalid": !isLoaded })}>
                        {renderBodyWrap()}
                    </div>
                </div>
                <Tooltip overlay={showOrders ? "Скрыть заказы" : "Отобразить заказы"}>
                    <div className="p-orders__ear" onClick={() => setShowOrders((s) => !s)}>
                        <i className={showOrders ? "icon-uEAAE-angle-right-solid" : "icon-uEACD-angle-left"} />
                    </div>
                </Tooltip>
            </Transition>
            {showOrders && <DistributionBids transition="half" back={() => setShowOrders((s) => !s)} />}
        </div>
    );
}

export default viewContainer(CityPlanning, "planning");
